import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  VStack,
  Text,
  FormErrorMessage,
  useToast,
  Alert,
  AlertIcon,
  Divider,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';

const AdminPasswordResetModal = ({ 
  isOpen, 
  onClose, 
  userType, // 'user', 'worker', or 'webuser'
  userId,
  userName,
  onSuccess,
  hasFastPassword = false // true for users and workers
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newFastPassword, setNewFastPassword] = useState('');
  const [confirmFastPassword, setConfirmFastPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showFastPassword, setShowFastPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const API_BASE_URL = process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL
    : process.env.REACT_APP_LOCAL_API_URL;

  const passwordsMatch = newPassword === confirmPassword;
  const fastPasswordsMatch = newFastPassword === confirmFastPassword;
  const isValidPassword = newPassword.length >= 6;
  const isValidFastPassword = /^\d{6}$/.test(newFastPassword);

  const handleReset = () => {
    setNewPassword('');
    setConfirmPassword('');
    setNewFastPassword('');
    setConfirmFastPassword('');
    setShowPassword(false);
    setShowFastPassword(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async () => {
    // Validation
    if (newPassword && !passwordsMatch) {
      toast({
        title: 'Erro',
        description: 'As senhas não coincidem',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (newPassword && !isValidPassword) {
      toast({
        title: 'Erro',
        description: 'A senha deve ter pelo menos 6 caracteres',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (hasFastPassword && newFastPassword && !fastPasswordsMatch) {
      toast({
        title: 'Erro',
        description: 'As senhas rápidas não coincidem',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (hasFastPassword && newFastPassword && !isValidFastPassword) {
      toast({
        title: 'Erro',
        description: 'A senha rápida deve ter exatamente 6 dígitos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!newPassword && !newFastPassword) {
      toast({
        title: 'Erro',
        description: 'Pelo menos uma senha deve ser fornecida',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      
      // Determine the API endpoint based on user type
      let endpoint = '';
      const payload = {};
      
      if (userType === 'webuser') {
        // For WebUsers, use the update endpoint with password
        endpoint = `${API_BASE_URL}api/webuser/${userId}`;
        payload.password = newPassword;
      } else if (userType === 'user') {
        endpoint = `${API_BASE_URL}api/user/admin-reset-password/${userId}`;
        if (newPassword) payload.newPassword = newPassword;
        if (newFastPassword) payload.newFastPassword = newFastPassword;
      } else if (userType === 'worker') {
        endpoint = `${API_BASE_URL}api/workeruser/admin-reset-password/${userId}`;
        if (newPassword) payload.newPassword = newPassword;
        if (newFastPassword) payload.newFastPassword = newFastPassword;
      }

      const method = userType === 'webuser' ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao redefinir senha');
      }

      const data = await response.json();
      
      // Show success message
      toast({
        title: 'Sucesso',
        description: data.message || 'Senha redefinida com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Show warning about email if it failed
      if (data.warning) {
        setTimeout(() => {
          toast({
            title: 'Aviso',
            description: data.warning,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }, 500);
      }

      handleClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Redefinir Senha</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Redefinindo senha para: <strong>{userName}</strong>
              </Text>
            </Alert>

            {/* Regular Password Section */}
            <VStack spacing={3} width="100%">
              <Text fontWeight="bold" alignSelf="flex-start">Senha Principal</Text>
              
              <FormControl isInvalid={newPassword && !isValidPassword}>
                <FormLabel>Nova Senha</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Digite a nova senha"
                  />
                  <InputRightElement>
                    <IconButton
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>A senha deve ter pelo menos 6 caracteres</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={confirmPassword && !passwordsMatch}>
                <FormLabel>Confirmar Senha</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Digite a senha novamente"
                  />
                  <InputRightElement>
                    <IconButton
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>As senhas não coincidem</FormErrorMessage>
              </FormControl>
            </VStack>

            {/* Fast Password Section (only for users and workers) */}
            {hasFastPassword && (
              <>
                <Divider />
                <VStack spacing={3} width="100%">
                  <Text fontWeight="bold" alignSelf="flex-start">Senha Rápida (6 dígitos)</Text>
                  
                  <FormControl isInvalid={newFastPassword && !isValidFastPassword}>
                    <FormLabel>Nova Senha Rápida</FormLabel>
                    <InputGroup>
                      <Input
                        type={showFastPassword ? 'text' : 'password'}
                        value={newFastPassword}
                        onChange={(e) => setNewFastPassword(e.target.value)}
                        placeholder="Digite 6 dígitos"
                        maxLength={6}
                      />
                      <InputRightElement>
                        <IconButton
                          size="sm"
                          onClick={() => setShowFastPassword(!showFastPassword)}
                          icon={showFastPassword ? <ViewOffIcon /> : <ViewIcon />}
                          aria-label={showFastPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>A senha rápida deve ter exatamente 6 dígitos</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={confirmFastPassword && !fastPasswordsMatch}>
                    <FormLabel>Confirmar Senha Rápida</FormLabel>
                    <InputGroup>
                      <Input
                        type={showFastPassword ? 'text' : 'password'}
                        value={confirmFastPassword}
                        onChange={(e) => setConfirmFastPassword(e.target.value)}
                        placeholder="Digite os 6 dígitos novamente"
                        maxLength={6}
                      />
                      <InputRightElement>
                        <IconButton
                          size="sm"
                          onClick={() => setShowFastPassword(!showFastPassword)}
                          icon={showFastPassword ? <ViewOffIcon /> : <ViewIcon />}
                          aria-label={showFastPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>As senhas rápidas não coincidem</FormErrorMessage>
                  </FormControl>
                </VStack>
              </>
            )}

            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                O usuário será notificado por email sobre a alteração da senha.
              </Text>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={
              (!newPassword && !newFastPassword) ||
              (newPassword && (!confirmPassword || !passwordsMatch || !isValidPassword)) ||
              (newFastPassword && (!confirmFastPassword || !fastPasswordsMatch || !isValidFastPassword))
            }
          >
            Redefinir Senha
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdminPasswordResetModal;